import type { OutputIntent } from '@/components/org-chat/plus-actions-combobox';
import {
  type OnboardingMediaCategory,
  isOnboardingMediaCategory,
} from '@/lib/uploads/onboarding-media-upload';
import type { OrgChatArtifactPanelState } from '@/components/org-chat/org-chat-types';
import type { OrgChatSendOptions } from '@/contexts/org-chat-interface-context';
import { VOICE_CHAT_ROUTE_PREFIX } from '@/convex/organizations/threads/voice_chat_routes.helpers';
export type TimePeriod = 'morning' | 'afternoon' | 'evening' | 'night';
export type ThreadVisibility = 'organization' | 'private';
export type DesignOutputIntent = Extract<
  OutputIntent,
  'whiteboard' | 'presentation' | 'document'
>;
export type DesignRecentThreadTargetData = {
  _creationTime?: number;
  routeRef?: string;
  title?: string;
  threadId?: string;
  updatedAt?: number;
};
export const DESIGN_THREAD_VISIBILITY_STORAGE_KEY = 'design.thread.visibility';
export const DESIGN_PROJECT_NONE_VALUE = '__none__';
export const DESIGN_PROJECT_CREATE_VALUE = '__create__';
export function createOrgThreadRouteId(entryPoint?: 'voice'): string {
  const prefix = entryPoint === 'voice' ? VOICE_CHAT_ROUTE_PREFIX : 'ot_';
  return `${prefix}${crypto.randomUUID()}`;
}
export type LegacyOnboardingGenerationIntent = {
  category: OnboardingMediaCategory;
  sourceType: 'upload' | 'preset';
  storageKey?: string | null;
  storageId?: string | null;
  fileName?: string;
  presetName?: string;
  presetId?: string;
  presetPrompt?: string;
  timestamp: number;
};
export const isUnknownRecord = (
  value: unknown
): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};
export const isLegacyOnboardingGenerationIntent = (
  value: unknown
): value is LegacyOnboardingGenerationIntent => {
  if (!isUnknownRecord(value)) return false;
  const optionalStringOrNull = (candidate: unknown): boolean =>
    candidate === undefined ||
    candidate === null ||
    typeof candidate === 'string';
  return (
    isOnboardingMediaCategory(value.category) &&
    (value.sourceType === 'upload' || value.sourceType === 'preset') &&
    typeof value.timestamp === 'number' &&
    Number.isFinite(value.timestamp) &&
    optionalStringOrNull(value.storageKey) &&
    optionalStringOrNull(value.storageId) &&
    optionalStringOrNull(value.fileName) &&
    optionalStringOrNull(value.presetName) &&
    optionalStringOrNull(value.presetId) &&
    optionalStringOrNull(value.presetPrompt) &&
    (value.sourceType !== 'upload' ||
      (typeof value.storageKey === 'string' &&
        value.storageKey.trim().length > 0) ||
      (typeof value.storageId === 'string' &&
        value.storageId.trim().length > 0))
  );
};
export function designClosedArtifactPanelState(): OrgChatArtifactPanelState {
  return {
    open: false,
    displayMode: 'closed',
  };
}
export function normalizeDesignTargetThreadRouteRefs(
  options: OrgChatSendOptions | undefined
): Array<string> {
  const refs = new Set<string>();
  for (const routeRef of [
    ...(options?.targetThreadRouteRefs ?? []),
    ...(options?.targetThreadRouteRef ? [options.targetThreadRouteRef] : []),
  ]) {
    const trimmed = routeRef.trim();
    if (trimmed.length > 0) {
      refs.add(trimmed);
    }
  }
  return Array.from(refs);
}
export function formatDesignRecentThreadTimestamp(
  timestamp: number | null | undefined,
  locale: string
): string | undefined {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return undefined;
  }

  const date = new Date(timestamp);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  return new Intl.DateTimeFormat(
    locale,
    sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric' }
  ).format(date);
}
